import React, { PureComponent } from 'react';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { formatNumericHumanDate, parseNumericHumanDate } from '~/DateTime';
import { DebounceInput } from '~/Debounce';
import { RecalculationWarning } from '~/SkMaxPrice/RecalculationWarning';
import DatePickerInput from '~/UI/DatePickerInput/DatePickerInput';
import Icon from '~/UI/Icon/Icon';
import defaultInputStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import SaveActivityIndicator, { Status } from '~/UI/SaveActivityIndicator/SaveActivityIndicator';
import Select from '~/UI/Select/Select';
import Tooltip from '~/UI/Tooltip';

import styles from './styles.less';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip/TailwindTooltip';

export interface Value {
	value: string;
	isValid: boolean | null;
}

export interface Values {
	packageSize: Value;
	historicProductDataViewDate: {
		value: string;
		dateValue: Date | null;
		isValid: boolean | null;
	};
	basketVersion: string;
	priceComparisonFxDate: {
		value: string;
		dateValue: Date | null;
		isValid: boolean | null;
	};
}

interface Props {
	values: Values;
	updateStatus: Status | null;
	versions: ReferenceBasketVersion[] | null;
	onChange(values: Values): void;
	onSave(): void;
}

export default class ParametersPanel extends PureComponent<Props> {
	private renderHistoricViewDateInput() {
		const { values, onChange } = this.props;
		const yearAgo = new Date();
		yearAgo.setDate(yearAgo.getDate() - 365 * 10);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-historic-date'>
				<span className={styles.parameterLabelText}>Reference date:</span>
				<div className={styles.dateParameterInput}>
					<DatePickerInput
						clearable
						dateValue={values.historicProductDataViewDate.dateValue}
						disabledDays={{ after: new Date(), before: yearAgo }}
						isInvalid={values.historicProductDataViewDate.isValid === false}
						placeholder='Today'
						styles={defaultInputStyles}
						value={values.historicProductDataViewDate.value}
						onChange={(value) => {
							onChange({
								...values,
								historicProductDataViewDate: {
									value,
									dateValue: parseNumericHumanDate(value),
									isValid: null,
								},
							});
						}}
					/>
				</div>
			</label>
		);
	}

	private renderVersionSelect() {
		const { versions, values, onChange } = this.props;
		const currentVersion = versions?.find((version) => version.id === values.basketVersion) ?? null;

		return (
			<label className={styles.parameterLabel} htmlFor='reference-basket-version'>
				<span className={styles.parameterLabelText}>Reference basket valid:</span>
				<div className={styles.parameterSelect}>
					<Select
						disabled={versions === null}
						id='reference-basket-version'
						styles={{ select: styles.select }}
						value={values.basketVersion}
						onChange={(event) => {
							onChange({ ...values, basketVersion: event.target.value });
						}}
					>
						{versions !== null
							? versions.map((version) => (
									<option key={version.id} value={version.id}>
										Since {formatNumericHumanDate(version.sinceDate)}
									</option>
							  ))
							: null}
					</Select>

					{currentVersion?.description && (
						<TailwindTooltip
							reference={<Icon name='violet-question-mark' />}
							displayClass='inline-block ml-2 h-4 align-middle'
							position='top'
						>
							{currentVersion.description}
						</TailwindTooltip>
					)}
				</div>
			</label>
		);
	}

	private rendePriceComparisonFxDateInput() {
		const { values, onChange } = this.props;
		const yearAgo = new Date();
		yearAgo.setDate(yearAgo.getDate() - 365 * 10);

		return (
			<label className={styles.parameterLabel} htmlFor='price-comparison-fx-date'>
				<span className={styles.parameterLabelText}>Price comparison - exchange rates:</span>
				<div className={styles.dateParameterInput}>
					<DatePickerInput
						clearable
						dateValue={values.priceComparisonFxDate.dateValue}
						disabledDays={{ after: new Date(), before: yearAgo }}
						isInvalid={values.priceComparisonFxDate.isValid === false}
						placeholder='Today'
						styles={defaultInputStyles}
						value={values.priceComparisonFxDate.value}
						onChange={(value) => {
							onChange({
								...values,
								priceComparisonFxDate: {
									value,
									dateValue: parseNumericHumanDate(value),
									isValid: null,
								},
							});
						}}
					/>
				</div>
			</label>
		);
	}

	render() {
		const { values, updateStatus, onChange, onSave } = this.props;

		return (
			<div className={styles.container} data-testid='parameters-panel'>
				<div className={styles.content}>
					<div className={styles.parameters}>
						<p className={styles.parametersDescription}>Max price calculation parameters:</p>

						{this.renderHistoricViewDateInput()}
						{this.renderVersionSelect()}

						<label className={styles.parameterLabel} htmlFor='reference-package-size'>
							<span className={styles.parameterLabelText}>Package size:</span>
							<div className={styles.parameterInput}>
								<DebounceInput
									value={values.packageSize.value}
									onBlur={onSave}
									onChange={(value) => {
										onChange({
											...values,
											packageSize: { value, isValid: null },
										});
									}}
								>
									<Input
										id='reference-package-size'
										isInvalid={values.packageSize.isValid === false}
										styles={defaultInputStyles}
									/>
								</DebounceInput>
							</div>
						</label>

						{this.rendePriceComparisonFxDateInput()}

						<label className={styles.parameterLabel}>
							<span className={styles.parameterLabelText}>Settings:</span>
							<div className={styles.parameterSelect}>
								<Select styles={{ select: styles.selectFull }}>
									<option>Průměr tří nejnižších cen</option>
									{/*<option>Osobitná cenová regulace</option> @TODO - temporarily disable, missing backend implementation*/}
								</Select>
							</div>
						</label>

						<SaveActivityIndicator hideAfterMilliseconds={3000} status={updateStatus} />
						<RecalculationWarning />
					</div>
				</div>
			</div>
		);
	}
}
